<template>
    <div v-if="question">
        <div class="tw-text-center tw-max-w-md tw-m-auto">
            <span v-if="activeSection.name" class="tw-text-gray-400">
                {{ $t("surveys.participate.sectionHeading") }}: {{ activeSection.name }}
            </span>
            <h2
                ref="questionTitle"
                class="tw-m-0 tw-text-lg md:tw-text-xl"
                :class="{ 'tw-text-left': hasLongTitle }"
            >
                {{ questionTitle }}
            </h2>

            <span
                v-if="allowsMultipleSelections"
                class="tw-mt-4 tw-inline-block tw-font-sans tw-text-xs tw-text-indigo-500 tw-leading-none"
            >
                <i class="fal fa-info-circle tw-mr-1" />
                {{ $t("surveys.participate.multiSelectHint") }}
            </span>
        </div>

        <div class="tw-flex tw-justify-center tw-items-center tw-mt-4 md:tw-mt-8">
            <component
                :is="constants.ANSWER_TYPE_COMPONENT_MAPPING[question.skill.answerType]"
                :key="question.skill._id"
                class="tw-flex-1 tw-bg-white tw-rounded-lg tw-p-4 md:tw-p-8 tw-shadow-sm tw-relative"
                :class="{
                    'tw-mb-8': showSmallConfirmAction,
                }"
                :question="question"
            >
                <transition name="fade">
                    <div
                        v-if="showSmallConfirmAction"
                        class="tw-absolute tw-hidden lg:tw-block tw-top-full tw-mt-2 tw-right-0"
                    >
                        <l-base-button
                            tabindex="0"
                            :button-types="['size-sm', 'framed']"
                            @click.native="onNavigateNextStep"
                            >{{ $t("surveys.participate.btnConfirm") }}</l-base-button
                        >
                    </div>
                </transition>
            </component>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import { ANSWER_TYPE_COMPONENT_MAPPING } from "@/config/survey-answer-session";

import LSurveyAnswerMultipleChoice from "@/components/LSurveyAnswer/LSurveyAnswerMultipleChoice";
import LSurveyAnswerStructured from "@/components/LSurveyAnswer/LSurveyAnswerStructured";
import LSurveyAnswerUnstructured from "@/components/LSurveyAnswer/LSurveyAnswerUnstructured";
import LBaseButton from "@/components/LBaseButton";

export default {
    components: {
        LSurveyAnswerUnstructured,
        LSurveyAnswerStructured,
        LSurveyAnswerMultipleChoice,
        LBaseButton,
    },
    props: {
        question: {
            type: Object,
            required: true,
        },
        companyName: {
            type: String,
            default: "",
        },
    },

    data() {
        return {
            hasLongTitle: false,
            constants: Object.freeze({
                ANSWER_TYPE_COMPONENT_MAPPING,
            }),
        };
    },
    computed: {
        ...mapGetters({
            activeSection: "surveys/answerSession/activeSection",
            hasGivenAnswer: "surveys/answerSession/hasGivenAnswer",
            activeInteractiveStepNumber: "surveys/answerSession/activeInteractiveStepNumber",
            lastInteractiveStepNumber: "surveys/answerSession/lastInteractiveStepNumber",
        }),
        questionTitle() {
            return this.question.skill.name?.replace("%ACCOUNTNAME%", this.companyName);
        },
        allowsMultipleSelections() {
            return !!this.question.skill?.allowMultipleAnswers;
        },
        showSmallConfirmAction() {
            return (
                this.hasGivenAnswer &&
                this.activeInteractiveStepNumber < this.lastInteractiveStepNumber
            );
        },
    },
    watch: {
        questionTitle: {
            immediate: true,
            handler(newValue) {
                // we need to wait for the rendering as the component is re-used
                this.$nextTick(() => {
                    if (!newValue) {
                        this.hasLongTitle = false;
                        return;
                    }

                    this.hasLongTitle =
                        newValue.length > 100 || this.$refs.questionTitle?.clientHeight >= 30;
                });
            },
        },
    },
    methods: {
        onNavigateNextStep() {
            this.$store.dispatch("surveys/answerSession/navigateToNextStep", {
                payload: { direction: 1 },
            });
        },
    },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
    opacity: 0;
}
</style>
